import PropTypes from 'prop-types';
import { useFieldArray, useWatch } from 'react-hook-form';

import { cn } from '#/lib/utils';
import { Input } from '#/modules/commonComponents/form/Input';
import { getNestedError } from '#/modules/commonComponents/form/formFieldUtils';
import { SALES_ORDER_LINES_TIP } from '#/modules/crmMgmt/salesOrder/schemas/salesOrderSchema';
import { formatDisplayAmount } from '#/utils/formatNumber';

/* eslint-disable max-len -- Tailwind arbitrary grid-cols must stay as one static class string */
const LINE_GRID_COLS =
  '@3xl/form-section:grid-cols-[minmax(14rem,1.4fr)_minmax(8rem,8rem)_minmax(8rem,8rem)_minmax(8rem,8rem)_minmax(12rem,1fr)]';
/* eslint-enable max-len */

const LINES_MIN_W = cn(
  'w-full',
  '@3xl/form-section:min-w-[54.5rem]',
);

const LINE_GRID_CLASS = cn(
  'min-w-full gap-3 px-3',
  '@3xl/form-section:grid',
  LINE_GRID_COLS,
);

const LINE_HEADER_CLASS = cn(
  LINE_GRID_CLASS,
  'hidden border-b border-gray-300 bg-gray-100 py-2',
  'text-xs font-medium text-gray-600',
  '@3xl/form-section:grid',
);

const LINE_ROW_CLASS = cn(
  LINE_GRID_CLASS,
  'grid grid-cols-1 py-3',
  '@3xl/form-section:items-center',
);

function formatProductLabel(product) {
  if (!product) {
    return '—';
  }
  const code = String(product.productNo ?? product.code ?? '').trim();
  const name = product.name ? String(product.name).trim() : '';
  if (code && name) {
    return `${code} ${name}`;
  }
  return code || name || '—';
}

const SalesOrderLineRow = ({
  index,
  register,
  errors,
  line,
}) => {
  const qtyId = `lines.${index}.qty`;
  const remarkId = `lines.${index}.remark`;
  const qtyError = getNestedError(errors, qtyId);
  const remarkError = getNestedError(errors, remarkId);

  return (
    <li className={LINE_ROW_CLASS}>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">產品</p>
        <p className="text-sm text-gray-900">
          {formatProductLabel(line?.product)}
        </p>
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">數量</p>
        <Input
          id={qtyId}
          type="number"
          min={0}
          step="any"
          aria-label={`數量 ${index + 1}`}
          aria-invalid={qtyError ? true : undefined}
          title={qtyError?.message || undefined}
          className="w-full"
          {...register(qtyId)}
        />
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">單價</p>
        <p className="text-sm tabular-nums text-gray-900">
          {formatDisplayAmount(line?.unitPrice)}
        </p>
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">小計</p>
        <p className="text-sm tabular-nums text-gray-900">
          {formatDisplayAmount(line?.totalAmount)}
        </p>
      </div>
      <div className="min-w-0">
        <p className="@3xl/form-section:hidden text-xs text-gray-500">列備註</p>
        <Input
          id={remarkId}
          type="text"
          aria-label={`列備註 ${index + 1}`}
          aria-invalid={remarkError ? true : undefined}
          title={remarkError?.message || undefined}
          className="w-full"
          {...register(remarkId)}
        />
      </div>
    </li>
  );
};

SalesOrderLineRow.propTypes = {
  index: PropTypes.number.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  line: PropTypes.object,
};

/**
 * 銷貨單明細編輯：不可新增／刪列、不可選產品／選庫；僅數量與列備註可改。
 */
const SalesOrderLinesEditor = ({
  control,
  register,
  errors,
}) => {
  const { fields } = useFieldArray({
    control,
    name: 'lines',
    keyName: 'fieldId',
  });
  const watchedLines = useWatch({ control, name: 'lines', defaultValue: [] });

  return (
    <section className="@container/form-section pb-6">
      <div className="form-block">
        <h2 className="form-block__title">銷貨明細</h2>
        <p className="mt-1 text-xs text-muted-foreground">
          {SALES_ORDER_LINES_TIP}
        </p>
      </div>

      <div className="mt-3">
        <div className="overflow-x-scroll rounded border border-gray-400/65">
          <div className={LINES_MIN_W}>
            <div className={LINE_HEADER_CLASS} aria-hidden>
              <span>產品</span>
              <span>數量</span>
              <span>單價</span>
              <span>小計</span>
              <span>列備註</span>
            </div>
            <ul className="m-0 list-none divide-y divide-dashed divide-gray-400/65 p-0">
              {fields.length === 0 ? (
                <li className="px-3 py-8 text-sm text-muted-foreground">
                  尚無銷貨明細
                </li>
              ) : (
                fields.map((field, index) => (
                  <SalesOrderLineRow
                    key={field.fieldId}
                    index={index}
                    register={register}
                    errors={errors}
                    line={watchedLines?.[index] ?? field}
                  />
                ))
              )}
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

SalesOrderLinesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
};

export default SalesOrderLinesEditor;
